import {
  ComponentDescriptorOrArray,
  getRendererFromDescriptor,
  useComparatorName,
  useCurrentStepIndex,
  useField,
  useFields,
  usePage,
  useWizardService,
} from '@kizi/comparator-core';
import {
  Button,
  Div,
  DivProps,
  Heading,
  HeadingProps,
  Label,
  LabelProps,
  Select,
} from '@kizi/core-components';
import {
  Checkbox,
  IconV2,
  MultiRangeSlider,
  RadioGroup,
} from '@kizi/shared-components';
import { debounce } from 'lodash';
import React, {
  MouseEventHandler,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Desktop, MobileTableSmallDesktop } from '../utils/breakpoint';
import { useFieldTracking } from '../utils/hooks';
import { withFormProps } from './HOCs/withFormProps';
import { FilterSkeletonLayout } from './Skeletons/FilterSkeleton';
import { Window } from './Window';

const LabelWithTooltip = withFormProps(Label);
const FormRadioGroup = withFormProps(RadioGroup);
const FormSelect = withFormProps(Select);

const cursorStyle = {
  cursor: 'pointer',
};

const FormCheckbox = withFormProps(Checkbox, {
  _containerProps: {
    variant: 'formGroupInlineReversed',
    alignContent: 'center',
  },
});

export interface FilterItem {
  label: string;
  value: number | string;
  optionTooltip?: string;
  fieldId?: string;
}

export interface FilterDependentField {
  dependenceProperty: string;
  dependenceValue: string;
}

export interface Filter {
  id: string;
  fields: FilterItem[];
  subtitle?: string;
  title: string;
  type: 'checkbox' | 'radio-group' | 'select' | 'component';
  tooltip?: string;
  isToggleVisible?: boolean;
  dependsOn?: FilterDependentField;
}

export interface FiltersProps {
  choices: Choices;
  data: Filter[];
  onApplyClick?: React.MouseEventHandler<HTMLButtonElement>;
  onChange: Function;
  onClose?: React.MouseEventHandler;
  hideResetFiltersOption?: boolean;
  onResetFilters: () => void;
  show?: boolean;
  withDesktop?: boolean;
  showIcon?: boolean;
  title?: string;
  headingProps?: HeadingProps;
  registerFiltersUsage?: any;
}

interface FilterComponent extends Filter {
  onChange: Function;
  onChildChange: Function;
  key: string;
  isOpen: boolean;
  selected: (string | number)[];
  toggleOpen: MouseEventHandler;
  minValue?: number;
  maxValue?: number;
  step?: number;
  unit?: string;
  unitPosition?: 'left' | 'right';
  componentProps?: ComponentDescriptorOrArray;
  containerProps?: DivProps;
  sectionHeader?: string;
  sectionHeaderIcon?: string;
  shortCheckboxesGroupAmount?: number;
  sectionSubtitle?: string;
  disableMaxSlider?: boolean;
  customValues?: [number];
  valueProps?: LabelProps;
  headingProps?: HeadingProps;
  children?: React.ReactChild;
  trackEvent: (fieldName: string) => (event: any) => void;
  registerFiltersUsage?: any;
  labelStyles?: object;
  currentFilterValues?: any;
  resetFilterValues?: any;
}

const componentType = {
  slider: ({
    id,
    onChange,
    selected,
    title,
    tooltip,
    sectionHeader,
    sectionHeaderIcon,
    minValue,
    maxValue,
    step,
    unit,
    unitPosition,
    disableMaxSlider = false,
    customValues,
    valueProps,
    headingProps,
    children,
    trackEvent,
    resetFilterValues,
    currentFilterValues,
  }: FilterComponent & {
    resetFilterValues: any;
    currentFilterValues: any;
  }) => {
    const currentFieldValue = currentFilterValues?.[id];
    const resetFieldValue = resetFilterValues?.[id];
    return (
      <Div key={`slider-${id}`}>
        {sectionHeader && (
          <Div display="flex" alignItems="center" gridGap={2} {...headingProps}>
            {sectionHeaderIcon && <IconV2 name={sectionHeaderIcon} size={24} />}
            <Heading variant="H4" as="h4">
              {sectionHeader}
            </Heading>
          </Div>
        )}
        <LabelWithTooltip
          _label={title}
          _containerProps={{
            variant: 'filterLabel',
            mb: 3,
          }}
          _tooltip={tooltip}
          id={id}
        />
        <MultiRangeSlider
          leftValue={+selected[0]}
          rightValue={+selected[1]}
          min={minValue}
          max={maxValue}
          step={step}
          unit={unit}
          unitPosition={unitPosition}
          onChange={debounce(({ min, max }) => {
            onChange([min, max]);
            trackEvent(id)({
              type: 'change',
              target: { name: id, value: `${min}-${max}`, localName: 'slider' },
              ga4event: 'filter_usage',
            });
          }, 1000)}
          currentValues={currentFieldValue}
          initialValues={resetFieldValue}
          containerProps={{ mb: 5 }}
          valuesProps={valueProps}
          disableMaxSlider={disableMaxSlider}
          customValues={customValues}
        />
        {children}
      </Div>
    );
  },
  checkbox: ({
    id,
    isOpen,
    fields,
    onChange,
    selected,
    title,
    tooltip,
    toggleOpen,
    sectionHeader,
    sectionHeaderIcon,
    shortCheckboxesGroupAmount = 10,
    isToggleVisible = false,
    headingProps,
    containerProps,
    trackEvent,
  }: FilterComponent) => {
    let itemsToShow = Object.assign([], fields);

    if (isToggleVisible && !isOpen) {
      itemsToShow = itemsToShow.slice(0, shortCheckboxesGroupAmount);
    }

    return (
      <Div key={`checkbox-${id}`} {...containerProps}>
        {sectionHeader && (
          <Div display="flex" alignItems="center" gridGap={2} {...headingProps}>
            {sectionHeaderIcon && <IconV2 name={sectionHeaderIcon} size={24} />}
            <Heading variant="H4" as="h4">
              {sectionHeader}
            </Heading>
          </Div>
        )}
        {title && (
          <LabelWithTooltip
            _containerProps={{
              variant: 'filterLabel',
            }}
            _label={title}
            _tooltip={tooltip}
            id={id}
          />
        )}
        <Div variant="checkboxGroup">
          {itemsToShow.map(
            (
              { label, value, optionTooltip, fieldId }: FilterItem,
              _key: number,
            ) => {
              // We need a fallbackId for dynamic fields from BE
              const fieldIdWithFallback =
                fieldId || `${id}-${label.replace(' ', '-')}`.toLowerCase();

              return (
                <FormCheckbox
                  checked={
                    selected.includes(value) ||
                    (typeof value === 'number' &&
                      selected.includes('' + value)) ||
                    JSON.stringify(selected) === value
                  }
                  key={`checkbox-${id}-${_key}`}
                  onClick={(e) => {
                    onChange(value);
                    trackEvent(id)({
                      type: 'click',
                      target: {
                        ...e.target,
                        value,
                        localName: 'checkbox',
                      },
                      ga4event: 'filter_usage',
                    });
                  }}
                  variant="main"
                  _label={label}
                  value={value}
                  _tooltip={optionTooltip}
                  id={fieldIdWithFallback}
                />
              );
            },
          )}
          {isToggleVisible && fields.length > shortCheckboxesGroupAmount && (
            <Button mt={3} variant="transparent" onClick={toggleOpen}>
              {isOpen ? 'Minder' : 'Meer'}
            </Button>
          )}
        </Div>
      </Div>
    );
  },
  'radio-group': ({
    id,
    fields,
    onChange,
    selected,
    title,
    tooltip,
    labelStyles,
    ...restProps
  }: FilterComponent) => {
    return (
      <FormRadioGroup
        containerProps={{
          flexDirection: 'row-reverse',
          justifyContent: 'flex-end',
          variant: 'radio',
        }}
        _label={title}
        _tooltip={tooltip}
        _labelProps={{ variant: 'heading', ...labelStyles }}
        name={`radio-group-${id}`}
        onChange={(e) => onChange((e.target as HTMLInputElement).value)}
        radios={fields.map((field: FilterItem) => {
          return {
            ...field,
            variant: 'main',
          };
        })}
        value={selected[0]}
        {...restProps}
      />
    );
  },
  select: ({
    id,
    fields,
    onChange,
    selected,
    subtitle,
    title,
    tooltip,
  }: FilterComponent) => {
    return (
      <Div variant="select" key={`select-${id}`}>
        <LabelWithTooltip
          _containerProps={{
            variant: 'filterLabel',
          }}
          _label={title}
          _tooltip={tooltip}
        />
        <Div variant="formGroup">
          <Label>{subtitle}</Label>
          <FormSelect
            onChange={(e) => onChange(e.target.value)}
            value={selected[0]}
          >
            {fields.map((field: FilterItem, key: number) => {
              return <option key={`select-${id}-option-${key}`} {...field} />;
            })}
          </FormSelect>
        </Div>
      </Div>
    );
  },
  component: ({
    componentProps,
    key,
    sectionHeader,
    sectionSubtitle,
    tooltip,
    id,
    containerProps,
    onChildChange: onChange,
  }: FilterComponent) => {
    return (
      <Div id={id} key={key} {...containerProps}>
        {sectionHeader && (
          <Heading variant="H4" as="h4">
            {sectionHeader}
          </Heading>
        )}
        {sectionSubtitle && (
          <LabelWithTooltip
            _containerProps={{
              variant: 'filterLabel',
            }}
            _label={sectionSubtitle}
            _tooltip={tooltip}
          />
        )}
        {componentProps &&
          getRendererFromDescriptor({
            ...componentProps,
            props: {
              ...componentProps['props'],
              onChange,
              id,
            },
          })}
      </Div>
    );
  },
};

export interface Choices {
  [key: string]: (string | number)[];
}

const areArraysEqual = (array1, array2): boolean =>
  JSON.stringify(array1) == JSON.stringify(array2);

const toggleArrayOfValues = (obj: Choices, id: string, val: string) => {
  if (areArraysEqual(obj[id], JSON.parse(val))) {
    // Reset array filter
    obj[id] = [];
  } else {
    obj[id].push(...JSON.parse(val));
  }
};

const isJson = (item) => {
  item = typeof item !== 'string' ? JSON.stringify(item) : item;

  try {
    item = JSON.parse(item);
  } catch (e) {
    return false;
  }

  if (typeof item === 'object' && item !== null) {
    return true;
  }

  return false;
};

export function getUpdatedChoices(
  choices: Choices,
  type: 'checkbox' | 'radio-group' | 'select' | 'component' | 'slider',
  id: string,
  val: string,
): {
  [x: string]: (string | number)[];
} {
  const obj = { ...choices };

  if (type === 'checkbox') {
    const idx = obj[id]?.indexOf(val);
    if (idx > -1) {
      obj[id].splice(idx, 1);
    } else {
      if (isJson(val)) {
        toggleArrayOfValues(obj, id, val);
      } else {
        if (obj[id]) {
          obj[id].push(val);
        } else {
          obj[id] = [val];
        }
      }
    }
  } else if (type === 'slider') {
    obj[id] = val as unknown as number[];
  } else {
    obj[id] = [val];
  }

  return obj;
}

export const Filters: React.FC<FiltersProps> = ({
  choices,
  data = [],
  onApplyClick,
  onChange,
  onClose = () => {
    // on close
  },
  hideResetFiltersOption = false,
  onResetFilters,
  show = false,
  withDesktop,
  showIcon = false,
  title = 'Filteren',
  headingProps,
  registerFiltersUsage,
  ...divProps
}: FiltersProps) => {
  const [checkboxesWithToggle, setCheckboxesWithToggle] = useState<string[]>(
    [],
  );
  const filters = useField('overview.filters');
  const defaultFilters = useField('overview.defaultFilters');
  const currentFilterValues = JSON.parse(filters?.value as string);
  const resetFilterValues = JSON.parse(defaultFilters?.value as string);

  const { trackEvent } = useFieldTracking();

  const toggleOpen = useCallback((id: string) => {
    setCheckboxesWithToggle((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  }, []);

  const isOpen = useCallback(
    (id: string) => {
      return checkboxesWithToggle.includes(id);
    },
    [checkboxesWithToggle],
  );

  const Components = useMemo(
    () =>
      data.map((props: Filter, index: number) => {
        const { id, type, dependsOn } = props;
        const componentMappingMatch = componentType[type];
        const key = `${type}-${index}`;

        if (
          dependsOn &&
          !choices[dependsOn.dependenceProperty]?.includes(
            dependsOn.dependenceValue,
          )
        ) {
          return null;
        }

        return componentMappingMatch({
          selected: choices[id] || [],
          ...props,
          key,
          isOpen: isOpen(id),
          toggleOpen: () => toggleOpen(id),
          onChange: (val) => {
            onChange(getUpdatedChoices(choices, type, id, val));
          },
          onChildChange: (_id, val) => {
            onChange(getUpdatedChoices(choices, type, _id, val));
          },
          id,
          trackEvent,
          registerFiltersUsage,
          currentFilterValues,
          resetFilterValues,
        });
      }),
    [choices, data, checkboxesWithToggle, currentFilterValues],
  );

  const innerComp = (
    <Div mb={[4, null, 0]} variant="filters">
      {Components}
    </Div>
  );

  const resetFilters = (
    <Button onClick={onResetFilters} style={cursorStyle} variant="transparent">
      Filters resetten
    </Button>
  );

  return (
    <>
      {withDesktop && (
        <Desktop>
          <Div
            bg="white"
            borderRadius={2}
            boxShadow="default"
            p={6}
            variant="filtersContainer"
            {...divProps}
            // display={[null, null, 'none']}
          >
            <Div
              display="flex"
              justifyContent="space-between"
              position="relative"
            >
              <Heading variant="H4" as="h4" my={1} {...headingProps}>
                {title}
              </Heading>
              {hideResetFiltersOption ? null : resetFilters}
            </Div>
            {innerComp}
          </Div>
        </Desktop>
      )}

      <MobileTableSmallDesktop>
        <Window
          showIcon={showIcon}
          bottomContent="Filters toepassen"
          onApply={onApplyClick}
          onClose={onClose}
          show={show}
          title={title}
          container={<MobileTableSmallDesktop children />}
        >
          <>
            {hideResetFiltersOption ? null : (
              <Div variant="mobileResetFiltersWrapper">{resetFilters}</Div>
            )}
            {innerComp}
          </>
        </Window>
      </MobileTableSmallDesktop>
    </>
  );
};

export interface FiltersWrapperProps extends FiltersProps {
  fields: string[];
  disableLoadingSkeleton?: boolean;
  withDesktop?: boolean;
  registerFiltersUsage?: any;
}

export const FiltersWrapper: React.FC<FiltersWrapperProps> = ({
  fields,
  disableLoadingSkeleton = false,
  withDesktop = true,
  showIcon = false,
  registerFiltersUsage,
  ...restProps
}) => {
  const { state } = usePage();
  const _fields = useFields(fields);
  if (!_fields) return null;
  const comparatorName = useComparatorName();
  const filtersFieldIndex = _fields.findIndex((f) => f?.name === 'filters');
  const choices = JSON.parse(_fields[filtersFieldIndex]?.value as string);
  const currentStepIndex = useCurrentStepIndex();
  const service = useWizardService();
  const isLoadingProducts = useField('overview.isLoadingProducts');
  const defaultDynamicField = useField(
    'overview.defaultDynamicFiltersToListen',
  );
  const defaultFiltersField = useField('overview.defaultFilters');
  if (
    !service ||
    !service.state.context.steps ||
    currentStepIndex === undefined
  )
    return null;
  const interpreter = service.state.context.steps[currentStepIndex];
  const showFilterSkeleton = isLoadingProducts?.value === 'true';

  function handleFilterReset() {
    const filtersIndex = interpreter
      .getSnapshot()
      ?.context.fields.findIndex((field) => field.name === 'filters');

    if (filtersIndex !== undefined && filtersIndex > -1) {
      const isHealthComparator = comparatorName.includes('health');
      const sendArray: any[] = [];

      if (isHealthComparator) {
        sendArray.push({
          type: 'SET_FORM_FIELDS',
          name: 'getUserInput',
          fields: [
            {
              name: 'coverageadditions',
              value: [] as any,
            },
          ],
        });
      }

      // Always include the overview fields, but conditionally include dynamicFiltersToListen
      const overviewFields: any[] = [
        {
          name: 'filters',
          value: defaultFiltersField?.value,
        },
      ];

      if (isHealthComparator) {
        overviewFields.unshift({
          name: 'dynamicFiltersToListen',
          value: defaultDynamicField?.value as string,
        });
      }

      sendArray.push({
        type: 'SET_FORM_FIELDS',
        name: 'overview',
        fields: overviewFields,
      });

      service?.send(sendArray);

      // Trigger the onChange callback to update visible products
      if (state?.onChange && defaultFiltersField?.value) {
        (state?.onChange as Function)(
          JSON.parse(defaultFiltersField?.value as string),
        );
      }
    }
  }
  if (!disableLoadingSkeleton && showFilterSkeleton) {
    return <FilterSkeletonLayout />;
  }
  return (
    <Filters
      {...restProps}
      data={state?.availableFilters as Filter[]}
      choices={choices}
      onChange={state?.onChange as Function}
      onResetFilters={handleFilterReset}
      withDesktop={withDesktop}
      showIcon={showIcon}
      registerFiltersUsage={registerFiltersUsage}
    />
  );
};

export default FiltersWrapper;
